/**
 * 积分相关API
 * 包括积分查询、消费和充值等功能
 */

import Logger from "../utils/logger";
import axiosInstance from "../services/axiosConfig";
import { API_PATHS, CREDITS_CONFIG } from "./config";
import { getCurrentUser, saveCurrentUser } from "./utils";

/**
 * 检查用户积分
 * @returns {Promise<Object>} 包含success、message和credits的响应对象
 */
export const checkUserCredits = async () => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return { success: false, message: "未登录，请先登录", data: 0 };
    }

    // 发送API请求获取最新积分
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.USER.CREDITS}/${currentUser.id}`
    );

    if (response.success) {
      // response.data 可能是数字(旧格式)或对象(新格式: {credits, isSuperVip, superVipEndAt})
      let credits = response.data;
      let isSuperVip = currentUser.isSuperVip || false;
      let superVipEndAt = currentUser.superVipEndAt || null;

      let bonusCredits = currentUser.bonusCredits || 0;
      if (typeof response.data === 'object' && response.data !== null) {
        credits = response.data.credits;
        isSuperVip = response.data.isSuperVip || false;
        superVipEndAt = response.data.superVipEndAt || null;
        bonusCredits = response.data.bonusCredits ?? bonusCredits;
      }

      // 更新本地用户信息中的积分、VIP状态和赠送积分
      const updatedUser = {
        ...currentUser,
        credits,
        isSuperVip,
        superVipEndAt,
        bonusCredits,
      };
      saveCurrentUser(updatedUser);
      window.dispatchEvent(
        new CustomEvent("userCreditsUpdated", { detail: { user: updatedUser } })
      );

      return { success: true, data: credits, isSuperVip, superVipEndAt, bonusCredits };
    } else {
      return {
        success: false,
        message: response.message,
        data: currentUser?.credits || 0,
      };
    }
  } catch (error) {
    console.log("checkUserCredits error", error);
    Logger.error("检查积分失败", error);
    // 如果API请求失败，返回本地存储的积分信息
    const currentUser = getCurrentUser();
    return {
      success: false,
      message: "无法连接到服务器，显示本地缓存的积分",
      data: currentUser?.credits || 0,
    };
  }
};

/**
 * 充值积分
 * @param {string} paymentMethod 支付方式
 * @param {number} amount 充值金额
 * @returns {Promise<Object>} 包含success、message和user的响应对象
 */
export const rechargeCredits = async (
  paymentMethod,
  amount = CREDITS_CONFIG.PAYMENT_AMOUNT
) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return { success: false, message: "未登录，请先登录" };
    }

    // 发送API请求
    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}${API_PATHS.USER.RECHARGE}`,
      {
        userId: currentUser.id,
        amount,
        paymentMethod,
      }
    );

    const data = response.data;

    if (response.success) {
      // 充值成功，更新本地用户信息
      saveCurrentUser(data.user);

      // 触发一个自定义事件，通知UI更新
      window.dispatchEvent(new CustomEvent("userCreditsUpdated"));

      Logger.info("用户充值成功", { userId: currentUser.id, amount });
    } else {
      Logger.warn("用户充值失败", {
        userId: currentUser.id,
        message: data.message,
      });
    }

    return data;
  } catch (error) {
    Logger.error("充值失败", error);
    return { success: false, message: "充值失败，请稍后再试" };
  }
};

export const confirmCreditCharge = async ({
  sessionId,
  resultType,
  resultRef,
} = {}) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.id) {
      return { success: false, message: "未登录，请先登录" };
    }
    if (!sessionId) {
      return { success: false, message: "缺少扣费会话" };
    }

    const response = await axiosInstance.post(
      `${API_PATHS.BASE_API_URL}/user/confirm-credit-charge`,
      {
        userId: currentUser.id,
        sessionId,
        resultType,
        resultRef,
      }
    );

    if (response.success) {
      await checkUserCredits();
    }

    return response;
  } catch (error) {
    Logger.error("确认扣费失败", error);
    return {
      success: false,
      message: error.message || "确认扣费失败",
      data: error.data || null,
    };
  }
};

/**
 * 管理员添加积分（此功能保留但改为与服务器交互）
 * @param {string} targetEmail 目标用户邮箱
 * @param {number} creditsToAdd 增加的积分数
 * @returns {Object} 包含success和message的响应对象
 */
export const adminAddCredits = (targetEmail, creditsToAdd) => {
  // 此功能在当前实现中不需要，管理员功能可以通过数据库直接操作
  return { success: false, message: "此功能已在后端实现，请联系系统管理员" };
};

export default {
  checkUserCredits,
  rechargeCredits,
  confirmCreditCharge,
  adminAddCredits,
};
